import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { getFilmsByIds, getSpecialById, getCategoryLabel } from "@/lib/films";

export default function Special() {
  const { id = "" } = useParams<{ id: string }>();
  const topic = getSpecialById(id);

  useSeo({
    title: topic ? `${topic.title} - 剧情专题 | 一路向西` : "剧情专题 | 一路向西",
    description: topic
      ? `${topic.title}：${topic.intro}`
      : "一路向西精选剧情专题，成体系地读懂影视作品。",
    keywords: topic ? `一路向西,剧情专题,${topic.title},影视推荐` : "一路向西,剧情专题,影视推荐",
  });

  if (!topic) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-bookmark-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该专题</h1>
        <p className="mt-3 text-sm text-foreground-600">该专题可能已下线，去首页看看当前精选专题吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
        >
          <i className="ri-home-4-line text-base" />
          返回首页
        </Link>
      </div>
    );
  }

  const topicFilms = getFilmsByIds(topic.filmIds);
  const relatedTopics = topic.relatedIds
    .map((relatedId) => getSpecialById(relatedId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return (
    <div className="w-full">
      <section className="relative h-[320px] w-full overflow-hidden bg-primary-950 md:h-[420px]">
        <img src={topic.cover} alt={`${topic.title}专题封面`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/92 via-primary-950/55 to-primary-950/40" />
        <div className="relative mx-auto flex h-full w-full max-w-6xl flex-col justify-end px-4 pb-8 md:px-6 md:pb-12">
          <Breadcrumb
            light
            items={[
              { label: "首页", to: "/" },
              { label: "剧情专题", to: "/#specials" },
              { label: topic.title },
            ]}
          />
          <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-bookmark-3-line text-sm" />
            {topic.subtitle}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {topic.title}
          </h1>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <p className="max-w-3xl text-sm leading-loose text-foreground-700 md:text-base">{topic.intro}</p>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-list-check-2 text-xl text-primary-600" />
              专题片单
            </h2>
            <ul className="mt-6 space-y-4">
              {topicFilms.map((film, index) => (
                <li key={film.id}>
                  <Link
                    to={`/detail/${film.id}`}
                    className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {index + 1}
                    </span>
                    <div className="h-28 w-20 shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-24">
                      <img
                        src={film.poster}
                        alt={`${film.title}海报`}
                        title={`${film.title} 剧情介绍`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading text-lg font-semibold text-foreground-950 group-hover:text-accent-700">
                          {film.title}
                        </h3>
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                          {getCategoryLabel(film.category)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-foreground-500">
                        {film.year} · {film.region} · {film.genres.join(" / ")}
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2 md:line-clamp-3">
                        {film.oneLine}
                      </p>
                      <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-medium text-accent-600">
                        查看剧情与角色
                        <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <aside className="space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-lightbulb-line text-lg text-accent-600" />
                推荐理由
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700">{topic.reason}</p>
            </section>

            {relatedTopics.length > 0 && (
              <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-links-line text-lg text-primary-600" />
                  相关专题
                </h2>
                <ul className="mt-4 space-y-3">
                  {relatedTopics.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/special/${item.id}`}
                        className="group flex items-center justify-between gap-3 rounded-md bg-background-100 px-3 py-3 transition-colors hover:bg-background-200"
                      >
                        <span className="flex flex-col">
                          <span className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-accent-700">
                            {item.title}
                          </span>
                          <span className="mt-0.5 text-xs text-foreground-500">{item.subtitle}</span>
                        </span>
                        <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}